import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  BackHandler,
  FlatList,
  Image,
  Pressable,
  StyleSheet,
  View,
  type ListRenderItemInfo,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  type ViewToken,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useVideoPlayer, VideoView } from 'expo-video';

import { HorizontalScrollView, Stack } from '@/components/layout';
import { SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import type { ReportAttachment } from '@/features/guardian-profile/schemas/guardian-report-issue-schema';
import { createStyles, useScaling } from '@/hooks';
import { colors, withOpacity } from '@/theme/colors';

const strings = STRINGS.guardian.reportIssue;

type ReportAttachmentPreviewModalProps = {
  attachments: ReportAttachment[];
  /** Index of the tile that was tapped — carousel opens here. */
  initialIndex: number;
  onClose: () => void;
};

/**
 * Plays only while this page is the active carousel slide.
 * `play()` is deferred: calling it in the same tick as mount no-ops because `VideoView` has not
 * registered with the player yet (`_mountedVideos` is still empty).
 */
function VideoPreview({ uri, active }: { uri: string; active: boolean }) {
  const styles = useStyles();
  const player = useVideoPlayer(uri, instance => {
    instance.loop = true;
  });

  useEffect(() => {
    if (!active) {
      player.pause();
      return;
    }

    let cancelled = false;
    const tryPlay = () => {
      if (cancelled) return;
      try {
        player.play();
      } catch {
        // Autoplay can be blocked; native controls still let the user start playback.
      }
    };

    // VideoView mounts after this effect's commit — retry once the element is attached.
    const raf = requestAnimationFrame(tryPlay);
    const timeout = setTimeout(tryPlay, 100);

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      clearTimeout(timeout);
      player.pause();
    };
  }, [active, player]);

  return (
    <VideoView
      player={player}
      style={styles.mediaFill}
      contentFit="contain"
      nativeControls
      playsInline
    />
  );
}

function PreviewPage({
  item,
  active,
  width,
  height,
}: {
  item: ReportAttachment;
  active: boolean;
  width: number;
  height: number;
}) {
  const styles = useStyles();
  const { scale } = useScaling();

  return (
    <View style={[styles.page, { width, height }]}>
      {item.kind === 'image' ? (
        <Image source={{ uri: item.uri }} resizeMode="contain" style={styles.mediaFill} />
      ) : active ? (
        <VideoPreview uri={item.uri} active={active} />
      ) : (
        <View style={styles.videoPlaceholder}>
          <SvgIcon name="IcCamera" size={scale(32)} color={colors['0']} />
        </View>
      )}
    </View>
  );
}

/**
 * Full-screen lightbox for report attachments. Swipe horizontally to move between items
 * (carousel). Rendered in `SheetContent`'s `overlay` slot so it covers the sheet itself.
 */
export function ReportAttachmentPreviewModal({
  attachments,
  initialIndex,
  onClose,
}: ReportAttachmentPreviewModalProps) {
  const styles = useStyles();
  const { scale, deviceWidth, deviceHeight } = useScaling();
  const insets = useSafeAreaInsets();
  const listRef = useRef<FlatList<ReportAttachment>>(null);

  const startIndex = Math.min(Math.max(initialIndex, 0), Math.max(attachments.length - 1, 0));
  const [index, setIndex] = useState(startIndex);

  useEffect(() => {
    const sub = BackHandler.addEventListener('hardwareBackPress', () => {
      onClose();
      return true;
    });
    return () => sub.remove();
  }, [onClose]);

  const onViewableItemsChanged = useRef(({ viewableItems }: { viewableItems: ViewToken[] }) => {
    const next = viewableItems[0]?.index;
    if (typeof next === 'number') setIndex(next);
  }).current;

  const viewabilityConfig = useRef({ viewAreaCoveragePercentThreshold: 60 }).current;

  const onMomentumScrollEnd = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const next = Math.round(event.nativeEvent.contentOffset.x / deviceWidth);
      if (next !== index && next >= 0 && next < attachments.length) setIndex(next);
    },
    [attachments.length, deviceWidth, index],
  );

  const renderItem = useCallback(
    ({ item, index: itemIndex }: ListRenderItemInfo<ReportAttachment>) => (
      <PreviewPage
        item={item}
        active={itemIndex === index}
        width={deviceWidth}
        height={deviceHeight}
      />
    ),
    [deviceHeight, deviceWidth, index],
  );

  const keyExtractor = useCallback((item: ReportAttachment) => item.uri, []);

  const current = attachments[index];
  if (!current || attachments.length === 0) return null;

  return (
    <View style={styles.root}>
      <FlatList
        ref={listRef}
        data={attachments}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        renderScrollComponent={HorizontalScrollView}
        horizontal
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        bounces={attachments.length > 1}
        initialScrollIndex={startIndex}
        getItemLayout={(_, i) => ({
          length: deviceWidth,
          offset: deviceWidth * i,
          index: i,
        })}
        onMomentumScrollEnd={onMomentumScrollEnd}
        onViewableItemsChanged={onViewableItemsChanged}
        viewabilityConfig={viewabilityConfig}
        windowSize={3}
        style={styles.list}
      />

      <Stack
        direction="row"
        align="center"
        justify="space-between"
        gap={scale(12)}
        style={[styles.header, { paddingTop: Math.max(insets.top, scale(12)) }]}>
        <Stack flex={1} gap={scale(2)}>
          <Text
            variant="Font Guardian App/Subtitle/Subtitle 14 Semibold"
            numberOfLines={1}
            style={styles.name}>
            {current.name}
          </Text>
          {attachments.length > 1 ? (
            <Text variant="Font Guardian App/Subtitle/Subtitle 12 Regular" style={styles.counter}>
              {index + 1} / {attachments.length}
            </Text>
          ) : null}
        </Stack>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={strings.closePreview}
          hitSlop={12}
          onPress={onClose}
          style={({ pressed }) => [styles.closeButton, pressed ? styles.pressed : null]}>
          <SvgIcon name="IcXClose" size={scale(22)} color={colors['0']} />
        </Pressable>
      </Stack>

      {attachments.length > 1 ? (
        <Stack
          direction="row"
          align="center"
          justify="center"
          gap={scale(6)}
          style={[styles.dots, { paddingBottom: Math.max(insets.bottom, scale(16)) }]}>
          {attachments.map((item, i) => (
            <View key={item.uri} style={[styles.dot, i === index ? styles.dotActive : null]} />
          ))}
        </Stack>
      ) : null}
    </View>
  );
}

const useStyles = createStyles(({ scale }) => ({
  root: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: colors['Grays/Black'],
  },
  list: {
    ...StyleSheet.absoluteFillObject,
  },
  page: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors['Grays/Black'],
  },
  mediaFill: {
    width: '100%',
    height: '100%',
  },
  videoPlaceholder: {
    flex: 1,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors['10'],
  },
  header: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 2,
    paddingHorizontal: scale(16),
    paddingBottom: scale(12),
    backgroundColor: withOpacity(colors['Grays/Black'], 0.45),
  },
  name: { color: colors['0'] },
  counter: { color: withOpacity(colors['0'], 0.7) },
  closeButton: {
    width: scale(36),
    height: scale(36),
    borderRadius: scale(18),
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: withOpacity(colors['0'], 0.16),
  },
  pressed: { opacity: 0.7 },
  dots: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 2,
    paddingTop: scale(12),
  },
  dot: {
    width: scale(6),
    height: scale(6),
    borderRadius: scale(3),
    backgroundColor: withOpacity(colors['0'], 0.35),
  },
  dotActive: {
    width: scale(16),
    backgroundColor: colors['0'],
  },
}));

export default ReportAttachmentPreviewModal;
